import {
	type CalibrationJSONObject,
	type CaptureFileJSONObject,
	type CollectionJSONObject,
	type CollectionSummaryJSONObject,
	type FormJSONObject,
} from '@ikegps/core-api';
import type { IkeFetchable } from '../../rest/fetchable';
import type { FormModel } from '../form/form';
import { IkeModelForm } from '../form/form.model';
import type { CollectionModel } from './collection.data';
import { IkeModelCollection } from './collection.model';

/**
 * Collections are loaded lazily, this is the Minimal interface to load a collection
 **/
export interface IkeCollectionLoader {
	Collection: {
		getByRevision(revision: string): IkeFetchable<CollectionJSONObject>;
		getModel(revision: string): IkeFetchable<CollectionModel>;
	};
	CollectionSummary: {
		getById(objectId: string): IkeFetchable<CollectionSummaryJSONObject>;
	};
	Form: {
		getById(id: string): IkeFetchable<FormJSONObject>;
		getByRevision(revision: string): IkeFetchable<FormJSONObject>;
		getModel(revision: string): IkeFetchable<FormModel>;
	};
	Capture: {
		getById(objectId: string): IkeFetchable<CaptureFileJSONObject>;
		getByUuid(uuid: string): IkeFetchable<CaptureFileJSONObject>;
	};
	Calibration: {
		getById(objectId: string): IkeFetchable<CalibrationJSONObject>;
	};
}

export interface IkeCollectionLoaderState {
	Calibration: CalibrationJSONObject[];
	Capture: CaptureFileJSONObject[];
	Collection: CollectionJSONObject[];
	Form: FormJSONObject[];
}

export class IkeCollectionLoaderFromState implements IkeCollectionLoader {
	collections: CollectionJSONObject[];
	collectionModel: CollectionModel[];

	forms: FormJSONObject[];
	formModel: FormModel[];

	captures: CaptureFileJSONObject[];
	calibrations: CalibrationJSONObject[];

	Collection = {
		getByRevision: (revision: string) => this.getBy(this.collections, 'revision', revision),
		getModel: (revision: string) =>
			this.getOrCreate(this.collectionModel, 'revision', revision, () => {
				const collectionJson = this.Collection.getByRevision(revision).value;
				const form = this.Form.getModel(collectionJson.formversion).value;
				const collection = new IkeModelCollection().read(collectionJson, form, this);
				return collection;
			}),
	};
	CollectionSummary = {
		getById: (objectId: string) => this.getSummary('objectId', objectId),
		getByUuid: (uuid: string) => this.getSummary('uuid', uuid),
	};
	Form = {
		getById: (id: string) => this.getBy(this.forms, 'objectId', id),
		getByRevision: (revision: string) => this.getBy(this.forms, 'version', revision),
		getModel: (revision: string) =>
			this.getOrCreate(this.formModel, 'version', revision, () =>
				new IkeModelForm().read(this.Form.getByRevision(revision).value)
			),
	};
	Capture = {
		getById: (objectId: string) => this.getBy(this.captures, 'objectId', objectId),
		getByUuid: (uuid: string) => this.getBy(this.captures, 'uuid', uuid),
	};
	Calibration = {
		getById: (objectId: string) => this.getBy(this.calibrations, 'objectId', objectId),
	};

	/** Will fetching objects throw errors if they cannot be found */
	isMissingAllowed: boolean = false;

	constructor(
		collections?: CollectionJSONObject[],
		forms?: FormJSONObject[],
		captures?: CaptureFileJSONObject[],
		calibrations?: CalibrationJSONObject[]
	) {
		this.setCollections(collections);
		this.setForms(forms);
		this.setCaptureCalibration(captures, calibrations);
	}

	fromState(state: IkeCollectionLoaderState) {
		this.setCollections(state.Collection);
		this.setForms(state.Form);
		this.setCaptureCalibration(state.Capture, state.Calibration);
	}

	setCollections(collections: CollectionJSONObject[] = []) {
		this.collections = collections.slice();
		this.collectionModel = [];
	}

	setForms(forms: FormJSONObject[] = []) {
		this.forms = forms.slice();
		this.formModel = [];
	}

	addForm(form: FormJSONObject) {
		this.addUnique(this.forms, form, 'version');
	}

	addCollection(collection: CollectionJSONObject) {
		this.addUnique(this.collections, collection, 'revision');
	}

	setCaptureCalibration(captures: CaptureFileJSONObject[] = [], calibrations: CalibrationJSONObject[] = []) {
		this.captures = captures.slice();
		this.calibrations = calibrations.slice();
	}

	addCaptureCalibration(capture: CaptureFileJSONObject, calibration?: CalibrationJSONObject) {
		this.addUnique(this.captures, capture, 'uuid');
		if (calibration != null) {
			this.addUnique(this.calibrations, calibration, 'objectId');
		}
	}

	setIsMissingAllowed(isMissingAllowed: boolean) {
		this.isMissingAllowed = isMissingAllowed;
	}

	/** Add a item to the array only if it doesn't exist already */
	private addUnique<T, K extends keyof T>(objects: T[], obj: T, key: K) {
		const value = obj[key];
		if (objects.find((f) => f[key] === value)) {
			return;
		}
		objects.push(obj);
	}

	private getBy<T, K extends keyof T>(objects: T[], key: K, value: T[K]): IkeFetchable<T> {
		const foundItem = objects.find((t) => t[key] === value);

		// IkeRest will return null, so should this
		if (foundItem == null && this.isMissingAllowed !== true) {
			throw new Error(`Unable to fetch ${key as string}:${value}`);
		}

		return new IkeFetchableStatic(foundItem);
	}

	private getOrCreate<T, K extends keyof T>(objects: T[], key: K, value: T[K], create: () => T): IkeFetchable<T> {
		try {
			return this.getBy(objects, key, value);
		} catch (_e) {
			objects.push(create());
			return this.getBy(objects, key, value);
		}
	}

	private getSummary<K extends keyof CollectionJSONObject>(key: K, value: CollectionJSONObject[K]) {
		const collection = this.getBy(this.collections, key, value).value;
		if (collection == null) {
			throw new Error(`Unable to find collection: ${key}:${value}`);
		}
		const collectionModel = this.Collection.getModel(collection.revision).value;
		if (!collectionModel) {
			throw new Error(`Unable to get collection model ${collection.revision}`);
		}
		return new IkeFetchableStatic(new IkeModelCollection().summary(collectionModel));
	}
}

export class IkeFetchableStatic<T> implements IkeFetchable<T> {
	hasFetched: boolean = true;
	isFetching: boolean = false;
	lastFetch: number = Date.now();
	error = null;
	value: T = null;
	promise: Promise<T>;
	constructor(obj: T) {
		this.value = obj;
		this.promise = Promise.resolve(obj);
	}
	refetch(): Promise<T> {
		return this.promise;
	}
	reset(): void {
		// ignore
	}
	fetch(): Promise<T> {
		return this.promise;
	}
	init(value: T): void {
		this.value = value;
		this.promise = Promise.resolve(value);
	}
}
